import { useState, useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import styles from "../../styles/portfolioManagerDashboard.module.scss";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import KpiInfoIcon from "./KpiInfoIcon";
import { KPI_INFO, formatCompactNumber } from "./kpiHelpers";

const LINES = [
  { key: "planned",   label: "Planned",   color: "#0d9f61", dashed: true  },
  { key: "actual",    label: "Actual",    color: "#342c82", dashed: false },
];

const settings = JSON.parse(localStorage.getItem("settings"));

const formatDecimal = (val) => formatCompactNumber(settings, val ?? 0);

function CustomXTick({ x, y, payload, data }) {
  const point = data?.find((d) => d.month === payload.value);
  const hasAnomaly = point && point.overrun > 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fontSize={10} fill="#9e9e9e" fontWeight={500}>
        {payload.value}
      </text>
      {hasAnomaly && (
        <foreignObject x={-7} y={16} width={14} height={14}>
          <TriangleAlert size={12} color="#dc2626" strokeWidth={2.5} />
        </foreignObject>
      )}
    </g>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const overrun = payload[0]?.payload?.overrun;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #eaebf0",
      borderRadius: 8,
      fontSize: 12,
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      padding: "8px 12px",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
          <span style={{ color: "#666" }}>{entry.name}:</span>
          <span style={{ fontWeight: 600 }}>{formatDecimal(entry.value)}h</span>
        </div>
      ))}
      {overrun > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, paddingTop: 6, borderTop: "1px solid #f0f0f0", color: "#dc2626" }}>
          <TriangleAlert size={12} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Overrun: {formatDecimal(overrun)}h</span>
        </div>
      )}
    </div>
  );
}

function ResourceCapacityPanel({ data = [] }) {
  const [visible, setVisible] = useState({ planned: true, actual: true });

  const toggle = (key) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  const processedData = useMemo(() => data.map((d) => ({
    ...d,
    overrun: Math.max(0, (d.actual ?? 0) - (d.planned ?? 0)),
  })), [data]);

  return (
    <div className={styles.analyticsPanel}>
      <KpiInfoIcon content={KPI_INFO.resourceCapacity} />
      {/* Header */}
      <div className={styles.panelHeader}>
        <span className={styles.label}>Resources Capacity</span>
      </div>

      {/* Legend — clickable */}
      <div className={styles.chartLegend}>
        {LINES.map(({ key, label, color }) => (
          <span
            key={key}
            className={styles.legendItem}
            onClick={() => toggle(key)}
            style={{ cursor: "pointer", opacity: visible[key] ? 1 : 0.35, userSelect: "none" }}
          >
            <span className={styles.legendDot} style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>

      {/* Chart */}
      <div className={styles.panelChartFull}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedData} margin={{ top: 8, right: 8, bottom: 20, left: 0}}>
            <defs>
              {LINES.map(({ key, color }) => (
                <linearGradient key={key} id={`rc_${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={color} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="month"
              tick={<CustomXTick data={processedData} />}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9e9e9e" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${formatDecimal(value ?? 0)}h`}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#c4c4c4", strokeWidth: 1, strokeDasharray: "4 3" }}
            />
            {LINES.map(({ key, color, dashed }) =>
              visible[key] ? (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  strokeWidth={key === "actual" ? 2 : 1.5}
                  strokeDasharray={dashed ? "5 3" : undefined}
                  fill={`url(#rc_${key})`}
                  dot={false}
                  activeDot={{ r: key === "actual" ? 3.5 : 3, fill: color, stroke: "#fff", strokeWidth: 2 }}
                />
              ) : null
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ResourceCapacityPanel;

